// What the app page shows to whom. The server decides rights; this only maps the role it reports
// to the tabs and actions, so a control never promises what the server would refuse.
export type AppTab = 'app' | 'data' | 'history' | 'versions';

/** `user` opens and uses the app; `editor` changes it in chat and sees Data/History/Versions; `owner` also resets. */
export type AppRole = 'user' | 'editor' | 'owner';

export type AppPageRights = {
  role: AppRole;
  /** The viewer holds the publisher permission at the company. */
  canPublish: boolean;
  /** Reported by the server; not read here: Share is the canvas header's one Share chip. */
  canShare?: boolean;
  canUnshare?: boolean;
};

/** Users see only the app; editors and owners see the working tabs. */
export function visibleTabs(rights: AppPageRights, options: { deck?: boolean } = {}): AppTab[] {
  if (rights.role === 'user') return ['app'];
  // A deck is its slides and their versions: no App data, no activity of an App being used.
  if (options.deck) return ['app', 'versions'];
  return ['app', 'data', 'history', 'versions'];
}

export type AppAction = 'duplicate' | 'reset' | 'unshare' | 'change-in-chat' | 'open-chat';

/** The actions on offer. Unshare follows the right the server reports; users get nothing else. */
export function availableActions(rights: AppPageRights): AppAction[] {
  const actions: AppAction[] = [];
  if (rights.canUnshare === true) actions.push('unshare');
  if (rights.role === 'user') return actions;
  actions.push('change-in-chat', 'open-chat', 'duplicate');
  if (rights.role === 'owner') actions.push('reset');
  return actions;
}

/** A tab the person cannot see is never the active one: fall back to App. */
export function activeTab(
  requested: AppTab,
  rights: AppPageRights,
  options: { deck?: boolean } = {},
): AppTab {
  return visibleTabs(rights, options).includes(requested) ? requested : 'app';
}

/** Arrow/Home/End movement across the visible tabs. */
export function nextTab(tabs: readonly AppTab[], current: AppTab, key: string): AppTab | null {
  const index = tabs.indexOf(current);
  if (index < 0) return null;
  const last = tabs.length - 1;
  if (key === 'ArrowRight' || key === 'ArrowDown') return tabs[index === last ? 0 : index + 1];
  if (key === 'ArrowLeft' || key === 'ArrowUp') return tabs[index === 0 ? last : index - 1];
  if (key === 'Home') return tabs[0];
  if (key === 'End') return tabs[last];
  return null;
}
